import type { ListProps } from '@portals/core/src/components/List/List';
import type { OpenerHeadProps } from '@portals/core/src/components/Opener/Opener';
import dynamic from 'next/dynamic';

import type { AppFeaturePreviewProps } from '../components/AppFeaturePreview/AppFeaturePreview';
import type { HeaderButtonListProps } from '../components/HeaderButtonList/HeaderButtonList';
import type { Level3NavigationProps } from '../components/Level3Navigation/Level3Navigation';

export const FinalContainer = dynamic(() =>
  import('@portals/core/src/components/FinalGrid/FinalGrid').then((mod) => mod.FinalContainer)
);

export const FinalGridContainer = dynamic(() =>
  import('@portals/core/src/components/FinalGrid/FinalGrid').then((mod) => mod.FinalGridContainer)
);

export const SiteSearchMeta = dynamic(() =>
  import('../components/SiteSearchMeta/SiteSearchMeta').then((mod) => mod.SiteSearchMeta)
);
export const HeaderButtonList = dynamic<HeaderButtonListProps>(() =>
  import('../components/HeaderButtonList/HeaderButtonList').then((mod) => mod.HeaderButtonList)
);
export const OnSiteSearchV2 = dynamic(() =>
  import('../containers/OnSiteSearchV2/OnSiteSearchV2').then((mod) => mod.OnSiteSearchV2)
);
export const LegalFooter = dynamic(() =>
  import('../components/LegalFooter/LegalFooter').then((mod) => mod.LegalFooter)
);

export const FinalGridItem = dynamic(() =>
  import('@portals/core/src/components/FinalGrid/FinalGrid').then((mod) => mod.FinalGridItem)
);

export const AdviceBox = dynamic(() => import('../components/AdviceBox/AdviceBox').then((mod) => mod.AdviceBox));

export const AdviceBoxUI = dynamic(() =>
  import('@portals/core/src/components/AdviceBox/AdviceBox').then((mod) => mod.AdviceBox)
);

export const CooperationInfobox = dynamic(() =>
  import('../components/CooperationInfobox/CooperationInfobox').then((mod) => mod.CooperationInfobox)
);

export const CTASection = dynamic(() => import('../components/CTASection/CTASection').then((mod) => mod.CTASection));

export const Breadcrumbs = dynamic(() =>
  import('../components/Breadcrumbs/Breadcrumbs').then((mod) => mod.Breadcrumbs)
);

export const Button = dynamic(() =>
  import('../components/ContentButton/ContentButton').then((mod) => mod.ContentButton)
);

export const AccessibilityLinks = dynamic(() =>
  import('../components/AccessibilityLinks/AccessibilityLinks').then((mod) => mod.AccessibilityLinks)
);

export const ComparisonTable = dynamic(() =>
  import('../components/ComparisonTable/ComparisonTable').then((mod) => mod.ComparisonTable)
);

export const IconButton = dynamic(() =>
  import('../components/InternIconLink/InternIconLink').then((mod) => mod.InternIconLink)
);

export const TeaserGallery = dynamic(() =>
  import('@portals/core/src/containers/TeaserGallery/TeaserGallery').then((mod) => mod.TeaserGallery)
);
export const Card = dynamic(() => import('@portals/core/src/components/Card/Card').then((mod) => mod.Card));
export const CardHeadlineHeader = dynamic(() =>
  import('@portals/core/src/components/Card/Card').then((mod) => mod.CardHeadlineHeader)
);
export const CardTagHeader = dynamic(() =>
  import('@portals/core/src/components/Card/Card').then((mod) => mod.CardTagHeader)
);
export const CardContent = dynamic(() =>
  import('@portals/core/src/components/Card/Card').then((mod) => mod.CardContent)
);
export const TopInfoBox = dynamic(() =>
  import('../components/TopInfoBox/TopInfoBox').then((module) => module.TopInfoBox)
);
export const PortalsTopInfoBox = dynamic(() =>
  import('@portals/core/src/containers/TopInfoBox/TopInfoBox').then((module) => module.TopInfoBox)
);
export const TopInfoBoxItemText = dynamic(() =>
  import('@portals/core/src/containers/TopInfoBox/TopInfoBox').then((module) => module.TopInfoBoxItemText)
);
export const Typography = dynamic(() =>
  import('@portals/core/src/components/Typography/Typography').then((mod) => mod.Typography)
);
export const Pill = dynamic(() => import('@portals/core/src/components/Pill/Pill').then((mod) => mod.Pill));
export const ProductLink = dynamic(() =>
  import('../components/InternProductLink/InternProductLink').then((mod) => mod.InternProductLink)
);
export const UserContext = dynamic(() =>
  import('../components/UserContext/UserContext').then((mod) => mod.UserContext)
);
export const ProductLinkList = dynamic(() =>
  import('../components/ProductLinkList/ProductLinkList').then((mod) => mod.ProductLinkList)
);

export const SimpleTable = dynamic(() =>
  import('../components/SimpleTable/SimpleTable').then((mod) => mod.SimpleTable)
);

export const List = dynamic<ListProps>(() => import('@portals/core/src/components/List/List').then((mod) => mod.List));
export const ListItem = dynamic(() => import('@portals/core/src/components/List/List').then((mod) => mod.ListItem));
export const ListItemAvatar = dynamic(() =>
  import('@portals/core/src/components/List/List').then((mod) => mod.ListItemAvatar)
);
export const ListItemText = dynamic(() =>
  import('@portals/core/src/components/List/List').then((mod) => mod.ListItemText)
);

export const IconList = dynamic(() =>
  import('@portals/core/src/containers/IconList/IconList').then((mod) => mod.IconList)
);

export const ServiceLink = dynamic(() =>
  import('../components/InternServiceLink/InternServiceLink').then((mod) => mod.InternServiceLink)
);

export const Spacer = dynamic(() => import('@portals/core/src/components/Spacer/Spacer').then((mod) => mod.Spacer));
export const ServiceLinkList = dynamic(() =>
  import('../components/ServiceLinkList/ServiceLinkList').then((mod) => mod.ServiceLinkList)
);

export const AppCategoryLinkList = dynamic(() =>
  import('../components/AppCategoryLinkList/AppCategoryLinkList').then((mod) => mod.AppCategoryLinkList)
);

export const CategoryLink = dynamic(() =>
  import('../components/InternCategoryLink/InternCategoryLink').then((mod) => mod.InternCategoryLink)
);

export const TooltipButton = dynamic(() =>
  import('@portals/core/src/components/TooltipButton/TooltipButton').then((mod) => mod.TooltipButton)
);

export const CategoryLinkList = dynamic(() =>
  import('../components/CategoryLinkList/CategoryLinkList').then((mod) => mod.CategoryLinkList)
);

export const Footer = dynamic(() => import('@portals/core/src/containers/Footer/Footer').then((mod) => mod.Footer));

export const FlexFooter = dynamic(() =>
  import('@portals/core/src/containers/FlexFooter/FlexFooter').then((mod) => mod.FlexFooter)
);

export const Header = dynamic(() => import('@portals/core/src/containers/Header/Header').then((mod) => mod.Header));
export const HeaderBar = dynamic(() => import('../components/HeaderBar/HeaderBar').then((mod) => mod.HeaderBar));

export const CategoryMenu = dynamic(() =>
  import('../components/CategoryMenu/CategoryMenu').then((mod) => mod.CategoryMenu)
);

export const Opener = dynamic(() => import('../components/Opener/Opener').then((mod) => mod.Opener));
export const OpenerHead = dynamic<OpenerHeadProps>(() =>
  import('@portals/core/src/components/Opener/Opener').then((mod) => mod.OpenerHead)
);
export const OpenerMedia = dynamic(() =>
  import('@portals/core/src/components/Opener/Opener').then((mod) => mod.OpenerMedia)
);

export const OpenerVideo = dynamic(() =>
  import('@portals/core/src/components/Opener/OpenerVideo/OpenerVideo').then((mod) => mod.OpenerVideo)
);
export const OpenerContent = dynamic(() =>
  import('@portals/core/src/components/Opener/Opener').then((mod) => mod.OpenerContent)
);
export const OpenerFoot = dynamic(() =>
  import('@portals/core/src/components/Opener/Opener').then((mod) => mod.OpenerFoot)
);
export const OpenerBody = dynamic(() =>
  import('@portals/core/src/components/Opener/Opener').then((mod) => mod.OpenerBody)
);
export const OpenerActions = dynamic(() =>
  import('@portals/core/src/components/Opener/Opener').then((mod) => mod.OpenerActions)
);

export const ContactForm = dynamic(() =>
  import('../containers/ContactForm/ContactForm').then((mod) => mod.ContactForm)
);
export const Info = dynamic(() => import('../components/Info/Info').then((mod) => mod.Info));

export const Preview = dynamic(() => import('../components/Preview/Preview').then((mod) => mod.Preview));

export const SiteSearch = dynamic(() => import('../containers/SiteSearch/SiteSearch').then((mod) => mod.SiteSearch));

export const SiteSearchSuggest = dynamic(() =>
  import('../containers/SiteSearchSuggest/SiteSearchSuggest').then((mod) => mod.SiteSearchSuggest)
);

export const Finder = dynamic(() => import('../containers/Finder/Finder').then((mod) => mod.Finder));

export const InstituteSpecificContent = dynamic(() =>
  import('../containers/InstituteSpecificContent/InstituteSpecificContent').then((mod) => mod.InstituteSpecificContent)
);

export const RepaymentCalculator = dynamic(() =>
  import('../containers/RepaymentCalculator/RepaymentCalculator').then((mod) => mod.RepaymentCalculator)
);

export const PrivateLoanCalculator = dynamic(() =>
  import('../containers/PrivateLoanCalculator/PrivateLoanCalculator').then((mod) => mod.PrivateLoanCalculator)
);

export const SaveplanCalculator = dynamic(() =>
  import('../containers/SaveplanCalculator/SaveplanCalculator').then((mod) => mod.SaveplanCalculator)
);

export const Level3Navigation = dynamic<Level3NavigationProps>(() =>
  import('../components/Level3Navigation/Level3Navigation').then((mod) => mod.Level3Navigation)
);

export const SparkasseAssignmentForm = dynamic(() =>
  import('../containers/SparkasseAssignmentForm/SparkasseAssignmentForm').then((mod) => mod.SparkasseAssignmentForm)
);

export const RentalCalculator = dynamic(() =>
  import('../containers/RentalCalculator/RentalCalculator').then((mod) => mod.RentalCalculator)
);

export const FuelCostCalculator = dynamic(() =>
  import('../containers/FuelCostCalculator/FuelCostCalculator').then((mod) => mod.FuelCostCalculator)
);
export const AccordionDownload = dynamic(() =>
  import('../components/AccordionDownload/AccordionDownload').then((mod) => mod.AccordionDownload)
);

export const PensionGapCalculator = dynamic(() =>
  import('../containers/PensionGapCalculator/PensionGapCalculator').then((mod) => mod.PensionGapCalculator)
);
export const HouseholdCalculator = dynamic(() =>
  import('../containers/HouseholdCalculator/HouseholdCalculator').then((mod) => mod.HouseholdCalculator)
);

export const BruttoNettoCalculator = dynamic(() =>
  import('../containers/BruttoNettoCalculator/BruttoNettoCalculator').then((mod) => mod.BruttoNettoCalculator)
);

export const DiscountCalculator = dynamic(() =>
  import('../containers/DiscountCalculator/DiscountCalculator').then((mod) => mod.DiscountCalculator)
);

export const HomeConstructionLoanCalculator = dynamic(() =>
  import('../containers/HomeConstructionLoanCalculator/HomeConstructionLoanCalculator').then(
    (mod) => mod.HomeConstructionLoanCalculator
  )
);

export const HighCharts = dynamic(() =>
  import('@portals/core/src/components/HighCharts/HighCharts').then((mod) => mod.HighCharts)
);

export const IbanCalculator = dynamic(() =>
  import('../containers/IbanCalculator/IbanCalculator').then((mod) => mod.IbanCalculator)
);

export const PensionCalculator = dynamic(() =>
  import('../containers/PensionCalculator/PensionCalculator').then((mod) => mod.PensionCalculator)
);

export const BudgetCalculator = dynamic(() =>
  import('../containers/BudgetCalculator/BudgetCalculator').then((mod) => mod.BudgetCalculator)
);

export const CapitalCalculator = dynamic(() =>
  import('../containers/CapitalCalculator/CapitalCalculator').then((mod) => mod.CapitalCalculator)
);

export const AdditionalCostCalculator = dynamic(() =>
  import('../containers/AdditionalCostCalculator/AdditionalCostCalculator').then((mod) => mod.AdditionalCostCalculator)
);

export const ServiceTeaserGallery = dynamic(() =>
  import('../components/ServiceTeaserGallery/ServiceTeaserGallery').then((mod) => mod.ServiceTeaserGallery)
);
export const WallTeaserGallery = dynamic(() =>
  import('../components/WallTeaserGallery/WallTeaserGallery').then((mod) => mod.WallTeaserGallery)
);
export const ContentIntro = dynamic(() =>
  import('../components/ContentIntro/ContentIntro').then((mod) => mod.ContentIntro)
);

export const InfoCard = dynamic(() => import('../components/InfoCard/InfoCard').then((mod) => mod.InfoCard));

export const PortraitBox = dynamic(() =>
  import('../components/PortraitBox/PortraitBox').then((mod) => mod.PortraitBox)
);

export const Accordion = dynamic(() =>
  import('@portals/core/src/components/Accordion/Accordion').then((mod) => mod.Accordion)
);
export const AccordionDetails = dynamic(() =>
  import('@portals/core/src/components/Accordion/Accordion').then((mod) => mod.AccordionDetails)
);
export const AccordionSummary = dynamic(() =>
  import('@portals/core/src/components/Accordion/Accordion').then((mod) => mod.AccordionSummary)
);

export const ImagesDecorative = dynamic(() =>
  import('../components/ImagesDecorative/ImagesDecorative').then((mod) => mod.ContentImagesDecorative)
);
export const ImagesInformative = dynamic(() =>
  import('../components/ImagesInformative/ImagesInformative').then((mod) => mod.ContentImagesInformative)
);

export const ImageWall = dynamic(() => import('../components/ImageWall/ImageWall').then((mod) => mod.ContentImageWall));

export const FAQ = dynamic(() => import('../components/FAQ/FAQ').then((mod) => mod.FAQ));
export const PriceEstimator = dynamic(() =>
  import('../components/PriceEstimator/PriceEstimator').then((mod) => mod.PriceEstimator)
);
export const Video = dynamic(() => import('../components/Video/Video').then((mod) => mod.Video));
export const Link = dynamic(() => import('../components/Link/Link').then((mod) => mod.Link));
export const RichTextRenderer = dynamic(() =>
  import('@portals/core/src/components/RichTextRenderer/RichTextRenderer').then((mod) => mod.RichTextRenderer)
);
export const Canonical = dynamic(() => import('../components/Canonical/Canonical').then((mod) => mod.Canonical));

export const Quote = dynamic(() => import('@portals/core/src/components/Quote/Quote').then((module) => module.Quote));

export const Avatar = dynamic(() => import('../components/Avatar/Avatar').then((module) => module.Avatar));
export const JobSearch = dynamic(() => import('../containers/JobSearch/JobSearch').then((module) => module.JobSearch));

export const Quiz = dynamic(() => import('../containers/Quiz/Quiz').then((module) => module.Quiz));

export const ServiceBoxDialog = dynamic(() =>
  import('../containers/ServiceBoxDialog/ServiceBoxDialog').then((module) => module.ServiceBoxDialog)
);

export const DividerLink = dynamic(() =>
  import('../components/DividerLink/DividerLink').then((mod) => mod.DividerLink)
);
export const Navigation = dynamic(() => import('../components/Navigation/Navigation').then((mod) => mod.Navigation));
export const BenefitsList = dynamic(() =>
  import('../components/BenefitsList/BenefitsList').then((mod) => mod.BenefitsList)
);
export const BenefitsListImage = dynamic(() =>
  import('../components/BenefitsListImage/BenefitsListImage').then((mod) => mod.BenefitsListImage)
);
export const BenefitsListItemText = dynamic(() =>
  import('@portals/core/src/containers/BenefitsList/BenefitsList').then((mod) => mod.BenefitsListItemText)
);
export const InPageNavigationPage = dynamic(() =>
  import('@portals/core/src/components/InPageNavigation').then((mod) => mod.Page)
);
export const NumberCounter = dynamic(() =>
  import('@portals/core/src/containers/NumberCounter/NumberCounter').then((mod) => mod.NumberCounter)
);
export const InPageNavigationOpener = dynamic(() =>
  import('../components/InPageNavigationOpener/InPageNavigationOpener').then((mod) => mod.InPageNavigationOpener)
);
export const Stepper = dynamic(() => import('@portals/core/src/components/Stepper/Stepper').then((mod) => mod.Stepper));
export const GooglePlayButton = dynamic(() =>
  import('@portals/core/src/components/AppButton').then((mod) => mod.GooglePlay)
);
export const AppStoreButton = dynamic(() =>
  import('@portals/core/src/components/AppButton').then((mod) => mod.AppStore)
);
export const AppFeaturePreview = dynamic<AppFeaturePreviewProps>(() =>
  import('../components/AppFeaturePreview/AppFeaturePreview').then((mod) => mod.AppFeaturePreview)
);
export const AppButton = dynamic(() => import('../components/AppButton/AppButton').then((mod) => mod.AppButton));
export const CiveySurvey = dynamic(() =>
  import('@portals/core/src/components/CiveySurvey/CiveySurvey').then((mod) => mod.CiveySurvey)
);

export const TrustElement = dynamic(() =>
  import('@portals/core/src/components/TrustElement/TrustElement').then((mod) => mod.TrustElement)
);

export const TrustImages = dynamic(() =>
  import('../components/TrustImages/TrustImages').then((mod) => mod.TrustImages)
);

export const Divider = dynamic(() => import('@portals/core/src/components/Divider/Divider').then((mod) => mod.Divider));
export const Linda = dynamic(() => import('../components/Linda/Linda').then((mod) => mod.Linda));
export const ContactBox = dynamic(() => import('../components/ContactBox/ContactBox').then((mod) => mod.ContactBox));

export const Audio = dynamic(() => import('../components/Audio/Audio').then((mod) => mod.Audio));

export const MenuLinkList = dynamic(() =>
  import('@portals/core/src/components/MenuLinkList/MenuLinkList').then((mod) => mod.MenuLinkList)
);

export const MenuLink = dynamic(() => import('../components/MenuLink/MenuLink').then((mod) => mod.MenuLink));

export const ProductComparison = dynamic(() =>
  import('../components/ProductComparison/ProductComparison').then((mod) => mod.ProductComparison)
);
export const GenericComparison = dynamic(() =>
  import('../components/GenericComparison/GenericComparison').then((mod) => mod.GenericComparison)
);
export const GenericComparisonEntry = dynamic(() =>
  import('../components/GenericComparisonEntry/GenericComparisonEntry').then((mod) => mod.GenericComparisonEntry)
);
export const GenericComparisonPriceFree = dynamic(() =>
  import('@portals/core/src/components/GenericComparison/GenericComparisonPrice/GenericComparisonPrice').then(
    (mod) => mod.GenericComparisonPriceFree
  )
);
export const GenericComparisonPricePriced = dynamic(() =>
  import('@portals/core/src/components/GenericComparison/GenericComparisonPrice/GenericComparisonPrice').then(
    (mod) => mod.GenericComparisonPricePriced
  )
);
export const QuickNavi = dynamic(() => import('../components/QuickNavi/QuickNavi').then((mod) => mod.QuickNavi));
export const SparkasseAssignmentBox = dynamic(() =>
  import('../components/SparkasseAssignmentBox/SparkasseAssignmentBox').then((mod) => mod.SparkasseAssignmentBox)
);
export const SCert = dynamic(() => import('@portals/core/src/components/SCert/SCert').then((mod) => mod.SCert));

export const PageLinkList = dynamic(() =>
  import('../components/PageLinkList/PageLinkList').then((mod) => mod.PageLinkList)
);

export const GirokontoConditions = dynamic(() =>
  import('../components/GirokontoConditions/GirokontoConditions').then((mod) => mod.GirokontoConditions)
);

export const GirokontoServices = dynamic(() =>
  import('../components/GirokontoServices/GirokontoServices').then((mod) => mod.GirokontoServices)
);

export const GirokontoBenefits = dynamic(() =>
  import('../components/GirokontoBenefits/GirokontoBenefits').then((mod) => mod.GirokontoBenefits)
);

export const GirokontoComparison = dynamic(() =>
  import('../components/GirokontoComparison/GirokontoComparison').then((mod) => mod.GirokontoComparison)
);
export const SparkasseAssignmentFormModal = dynamic(() =>
  import('../containers/SparkasseAssignmentForm/SparkasseAssignmentFormModal/SparkasseAssignmentFormModal').then(
    (mod) => mod.SparkasseAssignmentFormModal
  )
);
export const SplitPaper = dynamic(() => import('../components/SplitPaper/SplitPaper').then((mod) => mod.SplitPaper));
export const SeaExitBox = dynamic(() => import('../components/SeaExitBox/SeaExitBox').then((mod) => mod.SeaExitBox));
export const OpenerLandingPage = dynamic(() =>
  import('../components/OpenerLandingPage/OpenerLandingPage').then((mod) => mod.OpenerLandingPage)
);
export const ReactPlayer = dynamic(() => import('react-player/lazy'), { ssr: false });
export const DatawrapperCharts = dynamic(() =>
  import('../components/DatawrapperCharts/DatawrapperCharts').then((mod) => mod.DatawrapperCharts)
);

export const AppReview = dynamic(() => import('../components/AppReview/AppReview').then((mod) => mod.AppReview));

export const OpenerHeroPage = dynamic(() =>
  import('../components/OpenerHeroPage/OpenerHeroPage').then((mod) => mod.OpenerHeroPage)
);

export const OpenerContentPage = dynamic(() =>
  import('../components/OpenerContentPage/OpenerContentPage').then((mod) => mod.OpenerContentPage)
);

export const CreditCalculator = dynamic(() =>
  import('../containers/CreditCalculator/CreditCalculator').then((module) => module.CreditCalculator)
);

export const ProcessNavigation = dynamic(() =>
  import('../components/ProcessNavigation/ProcessNavigation').then((mod) => mod.ProcessNavigation)
);
